import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

export default function GuestLogin() {
  const [email, setEmail] = useState("");
  const [lastName, setLastName] = useState("");
  const [arrival, setArrival] = useState("");
  const [departure, setDeparture] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please enter a valid email address."); return; }
    if (!lastName.trim()) { toast.error("Please enter your last name."); return; }
    setBusy(true);
    try {
      await http.post("/api/auth/request-magic-link", {
        email,
        opt_in_drip: optIn,
        source: "guide",
        last_name: lastName.trim(),
        check_in: arrival || "",
        check_out: departure || "",
      });
      setSent(true);
      toast.success("Check your inbox — your guest guide link is on its way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6 py-16">
      <div data-testid="guest-login-card" className="w-full max-w-md bg-white rounded-2xl border border-[#0E2A47]/8 p-10 shadow-[0_4px_40px_rgba(14,42,71,0.06)]">
        <Link to="/" className="text-xs uppercase tracking-widest font-body text-[#0E2A47]/50 hover:text-[#1FB6C9]">← Grandview Estate</Link>
        <h1 className="mt-6 font-display text-3xl md:text-4xl text-[#0E2A47]">Access your guest guide</h1>
        <p className="mt-3 text-[#0E2A47]/70 font-body">Enter your reservation details and we'll email you a one-click link to your interactive welcome guide. No passwords, ever.</p>

        {sent ? (
          <div data-testid="magic-sent" className="mt-8 rounded-lg bg-[#F1E8DA]/60 border border-[#F1E8DA] p-6">
            <div className="flex items-center gap-3">
              <Mail size={22} className="text-[#1FB6C9]" />
              <div className="font-display font-medium text-[#0E2A47]">Check your inbox</div>
            </div>
            <p className="mt-3 text-sm text-[#0E2A47]/70 font-body">We just sent your guest guide link to <b>{email}</b>. Bookmark that email — you can reuse the same link anytime during your stay.</p>
            <button onClick={() => setSent(false)} data-testid="magic-resend" className="mt-6 text-sm text-[#1FB6C9] hover:underline">Use different details</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-body text-[#0E2A47]/50">Last name</span>
              <input
                data-testid="guest-lastname-input"
                type="text"
                required
                autoFocus
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="On your reservation"
                className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-white px-4 py-3 font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9] focus:ring-2 focus:ring-[#1FB6C9]/20"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-body text-[#0E2A47]/50">Arrival</span>
                <input
                  data-testid="guest-arrival-input"
                  type="date"
                  value={arrival}
                  onChange={(e) => setArrival(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-white px-3 py-3 font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9] focus:ring-2 focus:ring-[#1FB6C9]/20"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest font-body text-[#0E2A47]/50">Departure</span>
                <input
                  data-testid="guest-departure-input"
                  type="date"
                  value={departure}
                  onChange={(e) => setDeparture(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-white px-3 py-3 font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9] focus:ring-2 focus:ring-[#1FB6C9]/20"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-body text-[#0E2A47]/50">Email</span>
              <input
                data-testid="guest-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 w-full rounded-lg border border-[#0E2A47]/15 bg-white px-4 py-3 font-body text-[#0E2A47] outline-none focus:border-[#1FB6C9] focus:ring-2 focus:ring-[#1FB6C9]/20"
              />
            </label>
            <label className="flex items-start gap-3 cursor-pointer pt-1">
              <input
                data-testid="guest-optin-checkbox"
                type="checkbox"
                checked={optIn}
                onChange={(e) => setOptIn(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[#0E2A47]/25 text-[#1FB6C9] focus:ring-[#1FB6C9]/30"
              />
              <span className="text-sm text-[#0E2A47]/70 font-body leading-relaxed">
                Email me the best direct-booking rates, exclusive offers, and Grandview news. Unsubscribe anytime.
              </span>
            </label>
            <button
              data-testid="guest-login-submit"
              type="submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1FB6C9] px-8 py-4 font-display font-medium text-white shadow-lg shadow-[#1FB6C9]/25 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {busy ? <Loader2 className="animate-spin" size={18} /> : <>Send my guest guide link <ArrowRight size={18} /></>}
            </button>
            <p className="text-xs text-[#0E2A47]/50 font-body text-center">
              By continuing you agree to receive transactional email about your stay from Grandview Estate.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
